import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import FilterPanel from 'components/DashboardComponents/FilterPanel';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { SearchParams } from 'api/ResourceAPI';
import { SelectedFiltersList } from 'types/models/SelectedFiltersList';
import KpiMetricsWorkflow from './KpiMetricsTabPages/KpiMetricsWorkflow';
import KpiMetricsAcceptance from './KpiMetricsTabPages/KpiMetricsAcceptance';
import KpiMetricsPerformance from './KpiMetricsTabPages/KpiMetricsPerformance';
import DashboardTabServices from './hooks/DashboardTabServices';
import Loader from 'components/Loader';
import transformPendingData from './helpers/transformPendingData';
import aggregateDonutData from './helpers/aggregateDonutData';

// --------- SAME DATA AS YOUR CODE (unchanged) ----------

type Selected = { [key: string]: string | string[] };
type FormattedSelected = { [key: string]: string };

type Metric = { id: number; page_title: string };

interface KpiMetricsTabProps {
  pendingStartDate: string | undefined;
  pendingEndDate: string | undefined;
  setPendingStartDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPendingEndDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  metric: Metric | null;
  setMetric: React.Dispatch<React.SetStateAction<Metric | null>>;
  // filterValues: FilterValuesType;
  // setFilterValues: React.Dispatch<React.SetStateAction<FilterValuesType>>;
}

// Helper function to initialize graph data based on metric type
const getInitialGraphData = (metricId?: number): GraphData => {
  if (metricId === 2) {
    return {
      orders: {
        consultant_submission: [],
        dm_validation: [],
        client_acceptance: [],
        acceptance_data: [],
      },
    };
  }
  // Default for metric 1 and others
  return {
    orders: {
      consultant_submission: [],
      dm_validation: [],
      client_acceptance: [],
    },
  };
};

type FilterKeys =
  // | 'start_date'
  // | 'end_date'
  | 'order_status'
  | 'affair_otp'
  | 'customer_id'
  | 'order_id'
  | 'deliverable_status'
  | 'client_id'
  | 'consultant_id'
  | 'delivery_manager'
  | 'country';

type FilterValuesType = {
  [key in FilterKeys]?: any;
};

const KpiMetricsTab: React.FC<KpiMetricsTabProps> = ({
  pendingStartDate,
  pendingEndDate,
  setPendingStartDate,
  setPendingEndDate,
  metric,
  setMetric,
  // filterValues,
  // setFilterValues,
}) => {
  const { makeCall } = useApi();
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const { loadings } = useSelector((state) => state);
  const { filter, dashboard } = useSelector((state) => state);
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [showFilterPanel, setShowFilterPanel] = React.useState(true);
  const [_, setForceUpdate] = React.useState(0); // State to force re-render
  const [renderCount, setRenderCount] = React.useState(false);

  const [metricsList, setMetricsList] = React.useState<Metric[]>([]);
  const [filterValues, setFilterValues] = React.useState<FilterValuesType>(() => {
    // Try to restore from localStorage on initial load
    const saved = localStorage.getItem('dashboardFilterValues');
    return saved ? JSON.parse(saved) : {};
  });

  // Handler for date changes in filter panel
  const handleDateChange = () => {
    // if (date) {
    // const newDate = date ? date.format('YYYY-MM-DD') : '';
    // setFilterValues((prev: any) => ({ ...prev, [name]: newDate }));
    // setFilterValues([] as any); // Reset other filters on date change
    setForceUpdate((prev) => prev + 1); // Force re-render
    // dispatch(selectFilterAction({ [name]: [newDate] }));
    // }
  };

  // Clear localStorage when component unmounts
  useEffect(
    () => () => {
      localStorage.removeItem('dashboardFilterValues');
    },
    []
  );

  type Order = { mission_advancement_id: number; count: number };
  type GraphData = {
    orders: {
      consultant_submission: Order[];
      dm_validation: Order[];
      client_acceptance: Order[];
    };
    Message?: string;
  };

  const [graphData, setGraphData] = React.useState<GraphData>(() => getInitialGraphData());
  const [barChartData, setBarChartData] = React.useState<any[]>([]);
  const [gridData, setGridData] = React.useState<any[]>([]);

  const { fetchKpiMetricGraph, getKpiMetricSuperFilter, fetchKpiMetricGrid, downloadKpiGridExcel } =
    DashboardTabServices();

  // Reset graph data when metric changes
  useEffect(() => {
    if (metric?.id) {
      setGraphData(getInitialGraphData(metric.id));
    }
  }, [metric?.id]);

  const fetchKpiFilter = async () => {
    try {
      dispatch(addLoadingAction('dashboardSuperFilter'));
      const response = await getKpiMetricSuperFilter(pendingStartDate, pendingEndDate);
      if (response && response.metrics) {
        setMetricsList(response.metrics);
        // Only set metric if it's not already set (maintain previously selected metric)
        if (!metric && response.metrics[0]) {
          setMetric(response.metrics[0]);
        }
        if (_ !== 0) {
          setRenderCount(true);
          setForceUpdate(0); // Reset force update counter
        }
      }
    } catch (error) {
      console.error('Failed to fetch Super filter:', error);
    } finally {
      dispatch(removeLoadingAction('dashboardSuperFilter'));
    }
  };

  useEffect(() => {
    if (_ !== 0) fetchKpiFilter();
  }, [_]);

  useEffect(() => {
    fetchKpiFilter();
  }, []);

  async function formatSelected(selected: Selected): Promise<SelectedFiltersList> {
    const output: SelectedFiltersList = {};

    const formatValue = (value: string | string[]): string | null => {
      if (Array.isArray(value)) {
        const cleanArray = value.filter((v) => v !== undefined && v !== null && v !== '');

        if (cleanArray.length === 0) return null;

        // FIX: Return null instead of 'all' so the key is removed from the output
        if (cleanArray.length > 1 && cleanArray[0] === 'ALL') return null;

        return cleanArray.length === 0 ? cleanArray[0] : cleanArray.sort().join(',');
      }

      // Optional: Handle cases where a single string "ALL" is passed
      if (value === 'ALL' || !value || value.trim() === '') return null;
      return value;
    };

    Object.entries(selected).forEach(([key, val]) => {
      if (!key || key === 'undefined' || key === 'null') return;
      if (val === undefined || val === null) return;

      const formatted = formatValue(val);

      // Remove the key-value pair when the formatted value is 'ALL'.
      if (formatted !== null && formatted !== 'ALL') {
        output[key] = formatted;
      }
    });
    const result_output = {
      start_date: [pendingStartDate],
      end_date: [pendingEndDate],
      ...output,
    };
    return result_output;
  }

  useEffect(() => {
    setCurrentPage(dashboard?.currentPage ?? 0);
  }, [dashboard?.currentPage]);

  useEffect(() => {
    setPageSize(dashboard?.pageSize ?? 10);
  }, [dashboard?.pageSize]);

  const getKpiMetricGrid = useCallback(
    async (data_type: number, params: SelectedFiltersList) => {
      try {
        dispatch(addLoadingAction('dashboardKpiGrid'));
        const result = await fetchKpiMetricGrid(data_type, params);

        if (result) {
          setGridData(result);
          dispatch(removeLoadingAction('dashboardKpiGrid'));
        }
      } catch (error) {
        console.error('Failed to fetch dashboardKpiGrid:', error);
      } finally {
        // Stop loading
        dispatch(removeLoadingAction('dashboardKpiGrid'));
      }
    },
    [dispatch, fetchKpiMetricGrid]
  );

  const getKpiMetricGraph = useCallback(
    async (data_type: number, params: SelectedFiltersList) => {
      try {
        dispatch(addLoadingAction('dashboardKpiGraph'));
        const result = await fetchKpiMetricGraph(data_type, params);
        if (result) {
          setGraphData(result);
          if (data_type === 1) {
            setBarChartData(transformPendingData(result?.orders?.pending_data, data_type));
          } else if (data_type === 2) {
            setBarChartData(transformPendingData(result?.orders?.acceptance_data, data_type));
          } else if (data_type === 3) {
            setBarChartData(transformPendingData(result?.orders?.pending_data, data_type));
          }
          // const barData = data_type === 1 ? result?.orders?.pending_data : result?.orders?.acceptance_data;
          dispatch(removeLoadingAction('dashboardKpiGraph'));
        }
      } catch (error) {
        console.error('Failed to fetch dashboardKpiGraph:', error);
      } finally {
        // Stop loading
        dispatch(removeLoadingAction('dashboardKpiGraph'));
      }
    },
    [dispatch, fetchKpiMetricGraph]
  );
  const fetchDonutGrid = async () => {
    const params = await formatSelected(filter.selected);
    // derive sort params from dashboard state
    let sort_field: string | undefined;
    let sort_by: string | undefined;
    if (dashboard?.sortKpiMetricGridData && dashboard.sortKpiMetricGridData.length > 0) {
      const { field, sort } = dashboard.sortKpiMetricGridData[0];
      sort_field = field;
      sort_by = sort?.toUpperCase();
    }

    // Prefer dashboard-controlled pagination if present to avoid double-triggering
    const page = dashboard?.currentPage ?? currentPage;
    const size = dashboard?.pageSize ?? pageSize;

    const searchParams: any = sort_field
      ? {
          size,
          page,
          sort_field,
          sort_by,
          ...params,
        }
      : {
          size,
          page,
          ...params,
        };

    if (params?.start_date && params?.end_date) {
      await getKpiMetricGrid(metric?.id || 1, searchParams);
    }
  };

  useEffect(() => {
    if (metric !== null && window.location.pathname.includes('dashboard') && filterValues !== null) {
      fetchDonutGrid();
    }
  }, [
    metric,
    JSON.stringify(filter.selected),
    dashboard?.sortKpiMetricGridData,
    dashboard?.currentPage,
    dashboard?.pageSize,
  ]);

  const fetchDonutGraph = async () => {
    const params = await formatSelected(filter.selected);
    if (params?.start_date && params?.end_date) {
      await getKpiMetricGraph(metric?.id || 1, params);
      // await getKpiMetricGrid(metric?.id || 1, params);
    }
  };

  useEffect(() => {
    if (metric !== null && filterValues !== null) fetchDonutGraph();
  }, [metric, JSON.stringify(filter.selected)]);

  useEffect(() => {
    if (renderCount) {
      fetchDonutGraph();
      fetchDonutGrid();
      setRenderCount(false);
    }
  }, [renderCount]);

  // --- Aggregate Donut Data using helper (metric-aware) ---
  const { donutDataConsultant, donutDataDM, donutDataClient } = React.useMemo(
    () => aggregateDonutData(graphData?.orders || {}, metric?.id || 1),
    [graphData, metric?.id]
  );

  const handleDownloadXlsx = useCallback(async () => {
    try {
      const params = await formatSelected(filter.selected);

      dispatch(addLoadingAction('dashboardKpiExportXlsx'));

      const { data, filename } = await downloadKpiGridExcel(metric?.id || 1, params);

      if (!data) throw new Error('No XLSX data received');

      const a = document.createElement('a');
      a.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`;
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Download XLSX failed:', e);
    } finally {
      dispatch(removeLoadingAction('dashboardKpiExportXlsx'));
    }
  }, [filter.selected, metric?.id, dispatch, downloadKpiGridExcel]);

  // Now you can use donutDataConsultant, donutDataDM, and donutDataClient in your component render. '#c0c0c0',

  const COLORS_CONSULTANT = ['#61D2A4', '#FF6040'];
  const COLORS_DM = ['#FF6040', '#61D2A4', '#FFC107'];
  const COLORS_CLIENT = ['#FF6040', '#61D2A4', '#FFC107'];
  const COLORS_ACCEPTANCE_CLIENT = ['#FF6040', '#61D2A4', '#3B82F6', '#FFD200'];
  const RATING_COLORS = ['#60C39B', '#3C83F0', '#FFD200', '#FF9100', '#808080', '#E600E6', '#C0C0C0'];

  const isInitialLoading = loadings?.dashboardSuperFilter || metric === null;

  if (isInitialLoading) {
    return (
      <Box sx={{ height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader />
      </Box>
    );
  }
  const isGraphLoading = Boolean(loadings?.dashboardKpiGraph);

  return (
    <Grid container spacing={3} sx={{ flexGrow: 1, pt: 1, height: 'calc(100vh - 64px)' }}>
      {/* ----------- TOP FILTER ROW (Matches Screenshot) ----------- */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="metrics-select-label">{t('Metrics')}</InputLabel>
            <Select
              labelId="metrics-select-label"
              id="metrics-select"
              value={metric?.id || ''}
              label="Metrics"
              onChange={(e) => {
                const selected = metricsList.find((m) => m.id === e.target.value);
                if (selected) setMetric(selected);
              }}
              disabled={!metricsList || metricsList.length === 0}
            >
              {metricsList.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.page_title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#1A73E8',
              color: '#fff',
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: '#1669C1',
              },
            }}
            onClick={() => setShowFilterPanel((p) => !p)}
          >
            {/* <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloseIcon sx={{ fontSize: 14, color: 'white' }} />
            </Box> */}
            Filter
          </Button>
        </Box>
      </Grid>

      {/* ----------- MAIN CONTENT ----------- */}
      <Grid item xs={12} lg={showFilterPanel ? 9.25 : 12}>
        {isGraphLoading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.6)',
            }}
          >
            <Loader />
          </Box>
        ) : (
          <>
            {metric?.id === 1 && (
              <React.Suspense fallback={<Loader />}>
                <KpiMetricsWorkflow
                  donutDataConsultant={donutDataConsultant}
                  donutDataDM={donutDataDM}
                  donutDataClient={donutDataClient}
                  COLORS_CONSULTANT={COLORS_CONSULTANT}
                  COLORS_DM={COLORS_DM}
                  COLORS_CLIENT={COLORS_CLIENT}
                  barChartData={barChartData}
                  metric={metric}
                  data_table={gridData}
                  onDownloadXlsx={handleDownloadXlsx}
                  showDownload={Boolean((gridData as any)?.orders?.totalItems)}
                />
              </React.Suspense>
            )}

            {metric?.id === 2 && (
              <React.Suspense fallback={<Loader />}>
                <KpiMetricsAcceptance
                  donutDataClient={donutDataClient}
                  COLORS_CONSULTANT={COLORS_CONSULTANT}
                  COLORS_DM={COLORS_DM}
                  COLORS_CLIENT={COLORS_ACCEPTANCE_CLIENT}
                  barChartData={barChartData}
                  metric={metric}
                  data_table={gridData}
                  onDownloadXlsx={handleDownloadXlsx}
                  showDownload={Boolean((gridData as any)?.orders?.totalItems)}
                />
              </React.Suspense>
            )}

            {metric?.id === 3 && (
              <React.Suspense fallback={<Loader />}>
                <KpiMetricsPerformance
                  donutDataClient={donutDataClient}
                  COLORS_CONSULTANT={COLORS_CONSULTANT}
                  COLORS_DM={COLORS_DM}
                  COLORS_CLIENT={RATING_COLORS}
                  barChartData={barChartData}
                  metric={metric}
                  data_table={gridData}
                  onDownloadXlsx={handleDownloadXlsx}
                  showDownload={Boolean((gridData as any)?.orders?.totalItems)}
                />
              </React.Suspense>
            )}
          </>
        )}
      </Grid>

      {/* ----------- FILTER PANEL ----------- */}

      {showFilterPanel && (
        <Grid item xs={12} lg={2.5}>
          <FilterPanel
            pendingStartDate={pendingStartDate}
            pendingEndDate={pendingEndDate}
            setPendingStartDate={setPendingStartDate}
            setPendingEndDate={setPendingEndDate}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            handleDateChange={handleDateChange}
            setForceUpdate={setForceUpdate}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default KpiMetricsTab;
